import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { coalesce } from 'vscode/vscode/vs/base/common/arrays';
import { onUnexpectedExternalError } from 'vscode/vscode/vs/base/common/errors';
import { Iterable } from 'vscode/vscode/vs/base/common/iterator';
import { toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IChatWidgetService } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat.service';
import { ChatDynamicVariableModel } from 'vscode/vscode/vs/workbench/contrib/chat/browser/contrib/chatDynamicVariables';
import { ChatRequestVariablePart, ChatRequestDynamicVariablePart } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatParserTypes';

let ChatVariablesService = class ChatVariablesService {
    constructor(chatWidgetService) {
        this.chatWidgetService = chatWidgetService;
        this._resolver = ( new Map());
    }
    async resolveVariables(prompt, model, progress, token) {
        let resolvedVariables = [];
        const jobs = [];
        prompt.parts
            .forEach((part, i) => {
            if (part instanceof ChatRequestVariablePart) {
                const data = this._resolver.get(part.variableName.toLowerCase());
                if (data) {
                    const references = [];
                    const variableProgressCallback = (item) => {
                        if (item.kind === 'reference') {
                            references.push(item);
                            return;
                        }
                        progress(item);
                    };
                    jobs.push(data.resolver(prompt.text, part.variableArg, model, variableProgressCallback, token).then(values => {
                        resolvedVariables[i] = { name: part.variableName, range: part.range, values: values ?? [], references };
                    }).catch(onUnexpectedExternalError));
                }
            }
            else if (part instanceof ChatRequestDynamicVariablePart) {
                resolvedVariables[i] = { name: part.referenceText, range: part.range, values: part.data };
            }
        });
        await Promise.allSettled(jobs);
        resolvedVariables = coalesce(resolvedVariables);
        resolvedVariables.sort((a, b) => b.range.start - a.range.start);
        return {
            variables: resolvedVariables,
        };
    }
    async resolveVariable(variableName, promptText, model, progress, token) {
        const data = this._resolver.get(variableName.toLowerCase());
        if (!data) {
            return Promise.resolve([]);
        }
        return (await data.resolver(promptText, undefined, model, progress, token)) ?? [];
    }
    hasVariable(name) {
        return ( this._resolver.has(name.toLowerCase()));
    }
    getVariable(name) {
        return this._resolver.get(name.toLowerCase())?.data;
    }
    getVariables() {
        const all = ( Iterable.map(( this._resolver.values()), data => data.data));
        return Iterable.filter(all, data => !data.hidden);
    }
    getDynamicVariables(sessionId) {
        const widget = this.chatWidgetService.getWidgetBySessionId(sessionId);
        if (!widget || !widget.viewModel || !widget.supportsFileReferences) {
            return [];
        }
        const model = widget.getContrib(ChatDynamicVariableModel.ID);
        if (!model) {
            return [];
        }
        return model.variables;
    }
    registerVariable(data, resolver) {
        const key = data.name.toLowerCase();
        if (( this._resolver.has(key))) {
            throw ( new Error(`A chat variable with the name '${data.name}' already exists.`));
        }
        this._resolver.set(key, { data, resolver });
        return toDisposable(() => {
            this._resolver.delete(key);
        });
    }
};
ChatVariablesService = ( __decorate([
    ( __param(0, IChatWidgetService))
], ChatVariablesService));

export { ChatVariablesService };
